import React, { useState } from 'react';
import { LoggedInUserResponse } from '../../store/authStore';
import axiosClient from '../../lib/auth';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';

export default function ForgotPasswordGo() {
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [resetError, setResetError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetPasswordResponse, setResetPasswordResponse] = useState<string>('');

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError('');
    setResetError('');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setEmailError('Email is required');
      return;
    }

    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setResetError('');

    try {
      const response = await axiosClient.post<LoggedInUserResponse>('/Auth/Reset', {
        email: email.trim(),
        source: 'watchfulGo',
      });
      if (response.data.message) {
        setResetPasswordResponse(response.data.message);
      } else {
        setResetError('Failed to send reset email. Please try again.');
      }
    } catch (error) {
      console.error('Reset password failed:', error);
      let message = 'Failed to send reset email. Please try again.';
      if (
        axios.isAxiosError(error) &&
        error.response?.data &&
        (error.response.data as any).message
      ) {
        message = (error.response.data as any).message;
      }
      setResetError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (resetPasswordResponse) {
    return (
      <div className="flex min-h-svh flex-col bg-[#1a1a1a]">
        <header className="flex items-center border-b border-[#2E3835] px-5 py-3">
          <img src="/logo.svg" alt="Watchful" className="h-7 w-auto" />
        </header>

        <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center px-8 pt-10 pb-16 text-center">
          <CheckCircle2 className="text-lime mb-7 h-16 w-16" strokeWidth={1.5} />

          <h1 className="mb-3 text-3xl font-bold tracking-tight text-white">Check your email</h1>
          <p className="mb-12 max-w-xs text-base leading-relaxed text-white/60">
            {resetPasswordResponse}
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col bg-[#1a1a1a]">
      <header className="flex items-center border-b border-[#2E3835] px-5 py-3">
        <img src="/logo.svg" alt="Watchful" className="h-7 w-auto" />
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-8 pt-10 pb-16">
        <h1 className="mb-2 text-3xl font-bold tracking-tight text-white">
          Reset your <br />
          <span className="text-lime">password</span>
        </h1>
        <p className="mb-10 text-base leading-relaxed text-white/60">
          Enter your email and we'll send you a link to reset your password.
        </p>

        <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
          <div>
            <Label htmlFor="email" className="mb-2 block text-sm text-white/70">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              className="focus:border-lime w-full border-[#2E3835] bg-[#2a2a2a] p-3 text-base text-white shadow-sm focus:outline-none"
              placeholder="Enter your email"
              value={email}
              onChange={handleEmailChange}
              required
              disabled={isLoading}
              autoFocus
            />
            {emailError && <p className="mt-2 text-sm text-red-400">{emailError}</p>}
          </div>

          {resetError && (
            <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3">
              <p className="text-sm text-red-400">{resetError}</p>
            </div>
          )}

          <Button
            type="submit"
            className="bg-lime mt-2 w-full py-6 text-base font-semibold text-black"
            disabled={isLoading || !email}
          >
            {isLoading ? (
              <>
                Sending Reset Email...
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              </>
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
